import { createServerFn } from "@tanstack/react-start";
import { sql } from "drizzle-orm";
import { z } from "zod";

import { requireAuth } from "@/auth/middleware";
import { getCurrentUserId, getDb, schema } from "@/db/client";
import { sanitizeForSeroval } from "@/server/seroval-safe";
import { mergeSeed } from "./library-seed";
import { library as seedLibrary, type BlocksLibrary } from "./mocks";

/**
 * Хранение справочника блоков работ.
 *
 * Аня на демо: «они точно нужны, абсолютно точно… чтобы мы их могли
 * добавлять, то есть мы постепенно будем стандартизировать вот эти блоки
 * работ». Пока справочник лежал JSON-файлом в бандле, добавить блок можно
 * было только релизом.
 *
 * Одна строка на инстанс (0013_blocks_library.sql): это справочник агентства,
 * а не настройка пользователя. Первое чтение сеет его из
 * `mocks/blocks-library.json` — те самые 18 блоков, снятые скриптом с их
 * шаблонов. Дублировать их в SQL значит завести второй источник правды.
 *
 * В отличие от рейткарты справочник в смету не «замораживается»: блок,
 * попавший в калькулятор, копируется туда целиком со строками и ставками, и
 * дальнейшая правка справочника его уже не касается. Поэтому переименовать
 * или удалить блок здесь безопасно — собранные сметы не поедут.
 *
 * **Запись идёт с условием по отметке `updatedAt`**, как у смет. Справочник
 * правят с нескольких мест сразу — экран блоков, экран обучения, само
 * автоприменение на сервере, — и каждое шлёт документ целиком. Без условия
 * побеждал последний: выученные блоки, журнал решений и политика обучения
 * исчезали, а «Обучение» применяло их заново на следующем заходе. Конфликт —
 * не ошибка: вызывающий получает актуальный документ и может повторить
 * правку на нём.
 */

const LibraryPayload = z.object({
  data: z.record(z.string(), z.unknown()),
});

/** Запись не состоялась: в базе документ новее того, от которого правили. */
export interface LibraryConflict {
  conflict: true;
  current: BlocksLibrary;
}

/** Документ с новой отметкой — одно место, где она ставится. */
function stamped(next: BlocksLibrary): BlocksLibrary {
  return { ...next, updatedAt: new Date().toISOString() };
}

const _loadLibrary = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .handler(async (): Promise<BlocksLibrary> => {
    const db = getDb();
    const [row] = await db.select({ data: schema.blocksLibrary.data }).from(schema.blocksLibrary);
    if (row) {
      // Пополнение новыми блоками — когда мы разобрали очередные рабочие
      // калькуляторы и завели типы работ, которых в справочнике не было.
      // Только добавляет: правки агентства не трогаются.
      const merged = mergeSeed(row.data as BlocksLibrary);
      if (merged.changed) {
        console.info(
          `[library] пополнение справочника: блоков ${merged.addedBlocks.length}, ` +
            `типов проектов ${merged.addedTypes.length}`,
        );
        const filled = stamped(merged.library);
        await db
          .insert(schema.blocksLibrary)
          .values({ data: filled, updatedBy: getCurrentUserId() })
          .onConflictDoUpdate({
            target: schema.blocksLibrary.singleton,
            set: { data: filled, updatedAt: new Date().toISOString() },
          });
        return sanitizeForSeroval("loadLibrary", filled);
      }
      return sanitizeForSeroval("loadLibrary", merged.library);
    }

    const [seeded] = await db
      .insert(schema.blocksLibrary)
      .values({ data: seedLibrary, updatedBy: getCurrentUserId() })
      .returning({ data: schema.blocksLibrary.data });
    return sanitizeForSeroval("loadLibrary", seeded.data as BlocksLibrary);
  });

const _saveLibrary = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => LibraryPayload.parse(d))
  .handler(async ({ data }): Promise<BlocksLibrary | LibraryConflict> => {
    const db = getDb();
    const incoming = data.data as unknown as BlocksLibrary;
    const next = stamped(incoming);
    const base = incoming.updatedAt;

    // Пишем, только если в базе та же отметка, с которой читали. Документ без
    // отметки — записан до этой проверки, его перезаписываем.
    const [saved] = await db
      .update(schema.blocksLibrary)
      .set({ data: next, updatedBy: getCurrentUserId(), updatedAt: new Date().toISOString() })
      .where(
        base
          ? sql`coalesce(${schema.blocksLibrary.data}->>'updatedAt', ${base}) = ${base}`
          : sql`true`,
      )
      .returning({ data: schema.blocksLibrary.data });
    if (saved) return sanitizeForSeroval("saveLibrary", saved.data as BlocksLibrary);

    // Ноль строк — либо справочника ещё нет, либо его успели записать.
    const [current] = await db
      .select({ data: schema.blocksLibrary.data })
      .from(schema.blocksLibrary);
    if (!current) {
      const [created] = await db
        .insert(schema.blocksLibrary)
        .values({ data: next, updatedBy: getCurrentUserId() })
        .returning({ data: schema.blocksLibrary.data });
      // RLS вернёт ноль строк, если правки не разрешены: молча «сохранить» в
      // никуда хуже, чем сказать правду.
      if (!created) throw new Response("Forbidden", { status: 403 });
      return sanitizeForSeroval("saveLibrary", created.data as BlocksLibrary);
    }
    return sanitizeForSeroval("saveLibrary", {
      conflict: true as const,
      current: current.data as BlocksLibrary,
    });
  });

export const loadLibrary = _loadLibrary;
export const saveLibrary = _saveLibrary;
